const TermsOfHire = () => {
  return (
    <div className="bg-slate-900 min-h-screen pt-24 pb-16" data-testid="terms-of-hire-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <span className="inline-block text-xs tracking-widest text-gray-400 border border-slate-700 rounded-full px-4 py-1.5 mb-4">
            LEGAL
          </span>
          <h1 className="text-4xl sm:text-5xl heading-font text-white mb-2">Terms of Hire</h1>
          <p className="text-sm text-gray-500">Last updated: 22 April 2026</p>
        </div>

        <div className="prose-legal text-gray-300 space-y-8 leading-relaxed">
          <p>
            These Terms of Hire ("Terms") govern the rental of audio equipment and related services from Audio Apex, a trading name of GL Music Ltd ("we", "us", "our"), to you ("the Customer", "you"). By placing a booking with us, you agree to these Terms.
          </p>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">1. Definitions</h2>
            <ul className="space-y-2 text-gray-400 ml-2">
              <li><strong className="text-gray-300">Equipment</strong> — the audio equipment, sound systems, DJ gear, and any accessories we agree to hire to you.</li>
              <li><strong className="text-gray-300">Hire Period</strong> — the period from delivery/collection to return, as set out in your booking confirmation.</li>
              <li><strong className="text-gray-300">Booking</strong> — your confirmed reservation of Equipment and/or services.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">2. Bookings and Quotes</h2>
            <div className="space-y-3 text-gray-400">
              <p>2.1 Quotes are provided on request and are valid for 14 days unless stated otherwise.</p>
              <p>2.2 A Booking is confirmed only once we have issued a written booking confirmation and received the required booking deposit.</p>
              <p>2.3 We will make reasonable efforts to supply the Equipment specified, but reserve the right to substitute equipment of equivalent or higher specification where necessary.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">3. Payment</h2>
            <div className="space-y-3 text-gray-400">
              <p>3.1 All prices are quoted exclusive of VAT. VAT at the prevailing rate (currently 20%) is added at the point of payment.</p>
              <p>3.2 Your booking is confirmed only once the required payment has been received and your date secured. Unpaid bookings are not held.</p>
              <p>3.3 For fixed-price packages, full payment is taken at the time of booking.</p>
              <p>3.4 For the Flagship system, a deposit is taken at the time of booking to secure your date. The remaining balance, plus VAT, is invoiced separately and is due no later than 7 days before your event date.</p>
              <p>3.5 For the Live Band package, a deposit is taken at the time of booking to secure your date. The final price is confirmed following consultation, and the remaining balance, plus VAT, is invoiced separately and is due no later than 7 days before your event date.</p>
              <p>3.6 Deposits paid to secure a date are applied against the total price of your booking.</p>
              <p>3.7 Accepted payment methods are bank transfer and card.</p>
            </div>

            <p className="mt-4 text-gray-400"><strong className="text-gray-300">Security deposit.</strong> We require a refundable security deposit against damage or loss, held for the duration of the Hire Period and returned within 7 days of the Equipment being returned in good condition. The amount depends on the package hired and will be confirmed in your quote. As a guide:</p>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm border-collapse" data-testid="security-deposit-table">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 pr-4 text-gray-300 font-medium">Package level</th>
                    <th className="text-left py-2 text-gray-300 font-medium">Security deposit</th>
                  </tr>
                </thead>
                <tbody className="text-gray-400">
                  <tr className="border-b border-slate-800"><td className="py-2 pr-4">Essential / Intimate</td><td className="py-2">&pound;250</td></tr>
                  <tr className="border-b border-slate-800"><td className="py-2 pr-4">Premium</td><td className="py-2">&pound;400</td></tr>
                  <tr className="border-b border-slate-800"><td className="py-2 pr-4">Professional</td><td className="py-2">&pound;600</td></tr>
                  <tr className="border-b border-slate-800"><td className="py-2 pr-4">Elite</td><td className="py-2">&pound;1,000</td></tr>
                  <tr><td className="py-2 pr-4">Flagship</td><td className="py-2">&pound;1,500</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">4. Venue Access and Additional Crew</h2>
            <div className="space-y-3 text-gray-400">
              <p>4.1 Our prices assume reasonable load-in and load-out access at your venue, including step-free or lift access where equipment must be moved between floors.</p>
              <p>4.2 Where venue access is difficult, for example stairs without a lift or long carrying distances, additional crew may be required to deliver and set up safely.</p>
              <p>4.3 Any such requirement is assessed by us based on the venue access details you provide at the time of booking. Where additional crew is needed, a surcharge of between &pound;100 and &pound;150 per day applies.</p>
              <p>4.4 We will confirm any access surcharge with you in writing before your event. It is not charged at the time of booking, and no surcharge applies without our prior written confirmation.</p>
              <p>4.5 It is your responsibility to provide accurate venue access information at the time of booking. Where access proves materially more difficult on the day than described, we reserve the right to apply or adjust the surcharge accordingly.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">5. Cancellation and Changes</h2>
            <div className="space-y-3 text-gray-400">
              <div>
                <p className="mb-2"><strong className="text-gray-300">5.1 Cancellation by you:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>The deposit paid to secure your date is non-refundable, as it covers costs we commit to on your behalf once your date is reserved.</li>
                  <li>Cancellations made 28 days or more before your event date receive a full refund of any amounts paid above the deposit.</li>
                  <li>Cancellations made between 7 and 28 days before your event date receive a refund of 50% of the total booking price.</li>
                  <li>Cancellations made less than 7 days before your event date are non-refundable.</li>
                  <li>All cancellation requests must be made in writing to info@audioapex.co.uk.</li>
                </ul>
              </div>
              <p><strong className="text-gray-300">5.2 Changes:</strong> We will try to accommodate changes to your Booking subject to availability, but cannot guarantee them.</p>
              <p><strong className="text-gray-300">5.3 Cancellation by us:</strong> In the unlikely event we must cancel (e.g. due to circumstances beyond our control), we will refund all sums paid in full. Our liability is limited to that refund.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">6. Delivery, Setup, and Collection</h2>
            <div className="space-y-3 text-gray-400">
              <p>6.1 We will deliver, install, and collect the Equipment at the times agreed in your booking confirmation.</p>
              <p>6.2 You must ensure safe, reasonable access to the venue and a suitable, dry, and secure environment with adequate power supply.</p>
              <p>6.3 If delivery or collection is delayed or prevented by reasons within your control, additional charges may apply.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">7. Customer Responsibilities</h2>
            <p className="mb-3 text-gray-400">During the Hire Period, you are responsible for:</p>
            <div className="space-y-2 text-gray-400">
              <p>7.1 Using the Equipment safely and only for its intended purpose.</p>
              <p>7.2 Keeping the Equipment secure and protected from theft, weather, liquids, and misuse.</p>
              <p>7.3 Not modifying, repairing, or attempting to repair the Equipment.</p>
              <p>7.4 Ensuring only competent persons operate the Equipment (unless we provide an operator).</p>
              <p>7.5 Complying with all relevant venue rules, licences, and noise regulations.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">8. Damage, Loss, and Your Liability</h2>
            <div className="space-y-3 text-gray-400">
              <p>8.1 You are responsible for the Equipment from delivery until collection.</p>
              <p>8.2 You are liable for the cost of repair or full replacement (at current market value) of any Equipment that is lost, stolen, or damaged beyond fair wear and tear during the Hire Period. The security deposit will be applied first, and any shortfall remains payable by you.</p>
              <p>8.3 You must report any damage, loss, or theft to us as soon as reasonably possible.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">9. Insurance</h2>
            <div className="space-y-3 text-gray-400">
              <p>9.1 We strongly recommend that you arrange appropriate insurance for your event, including public liability cover for the event as a whole.</p>
              <p>9.2 You are responsible for the Equipment while it is in your possession and use, as set out in clause 8. We recommend you ensure adequate cover is in place for the Hire Period.</p>
              <p>9.3 Details of the insurance arrangements applying to equipment supplied as part of your Booking are available from us on request.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">10. Our Liability</h2>
            <div className="space-y-3 text-gray-400">
              <p>10.1 We will provide our services with reasonable care and skill.</p>
              <p>10.2 We are not liable for any indirect or consequential loss, including loss of profit, loss of enjoyment, or disruption to your event, except where caused by our negligence.</p>
              <p>10.3 Nothing in these Terms limits our liability for death or personal injury caused by our negligence, or for fraud, or for any liability that cannot be excluded by law.</p>
              <p>10.4 Subject to clause 10.3, our total liability under any Booking shall not exceed the total hire fee paid for that Booking.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">11. Events Beyond Our Control (Force Majeure)</h2>
            <p className="text-gray-400">
              We are not liable for any failure or delay caused by events beyond our reasonable control, including but not limited to severe weather, power failure, transport disruption, or government restrictions.
            </p>
          </section>

          <section>
            <h2 className="text-xl heading-font text-white mb-3">12. Governing Law</h2>
            <p className="text-gray-400">
              These Terms are governed by the laws of England and Wales, and any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section className="border-t border-slate-700 pt-6">
            <h2 className="text-xl heading-font text-white mb-3">13. Contact</h2>
            <p className="text-gray-400 text-sm">
              <strong className="text-gray-300">GL Music Ltd (trading as Audio Apex)</strong><br />
              Email: info@audioapex.co.uk<br />
              Phone: 020 8049 5480<br />
              Registered office: Flat 203, 50 Sulivan Road, London, England, SW6 3DX
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfHire;
